import React, { useState, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import {
  Box,
  Avatar,
  Typography,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
} from "@mui/material";
import { API_ENDPOINT } from "../../utils/constants";
import { Person2, Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { GlassButtonLoading } from "../../themes/UiElements";

const LookupProfile = ({ selectedContact }) => {
  const [tag, setTag] = useState("");
  const [tagging, setTagging] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [contactDetail, setContactDetail] = useState(null);

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/message/get-tagged-user?from=${selectedContact.from}`
  );
  const { data: GroupList, isLoading: isLoadingList } =
    useApiEp(`/user/group-list`);

  useEffect(() => {
    if (data && data?.tag) {
      setTag(data.tag);
    }
  }, [data]);

  const handleGroupSelect = async (groupName) => {
    setSelectedGroup(groupName);
    setModalOpen(false);
    setContactDetail(null);
    try {
      setLoadingDetail(true);
      const response = await axios.get(
        `${API_ENDPOINT}/user/get-contact-detail`,
        {
          params: { phoneNumber: selectedContact.from, groupName },
          ...getHeader(),
        }
      );
      setContactDetail(response.data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <Box bgcolor={"white"} width={300} borderLeft={"1px solid #ddd"}>
      {/* Avatar & Name */}
      <Box mt={4} justifyContent={"center"} display={"flex"}>
        <Avatar sx={{ bgcolor: "primary.main", height: 48, width: 48 }}>
          {selectedContact?.profileName?.charAt(0)?.toUpperCase()}
        </Avatar>
      </Box>
      <Box p={2} justifyContent={"center"}>
        <Typography
          variant="h6"
          noWrap
          textAlign={"center"}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedContact.profileName}
        </Typography>
        <div style={{ textAlign: "center", fontSize: "12px" }}>
          {selectedContact.from}
        </div>
      </Box>
      <Divider sx={{ mx: 1 }} />

      {/* Detail Lookup Button */}
      <Box display={"flex"} justifyContent={"center"} mt={1}>
        <GlassButtonLoading
          onClick={() => setModalOpen(true)}
          startIcon={<Person2 />}
          disabled={isLoadingList}
        >
          Detail Lookup
        </GlassButtonLoading>
      </Box>

      {/* Contact Detail Section */}
      {loadingDetail && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {contactDetail && !loadingDetail && (
        <Box mx={2} mt={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Contact Detail
            </Typography>
            {selectedGroup && (
              <Chip
                label={selectedGroup}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            {Object.entries(contactDetail).map(([key, value], index, arr) => (
              <Box key={key}>
                <Box
                  px={1.5}
                  py={1}
                  display="flex"
                  flexDirection="column"
                  gap={0.2}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "capitalize", fontWeight: 600 }}
                  >
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {value || "—"}
                  </Typography>
                </Box>
                {index < arr.length - 1 && <Divider />}
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {/* Tag Section */}
      <Box m={2}>
        <TextField
          fullWidth
          variant="outlined"
          label="Enter Tag"
          size="small"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          margin="normal"
        />
        <LoadingButton
          onClick={async () => {
            const payload = { phoneNumber: selectedContact.from };
            if (tag) payload.tag = tag.trim();
            try {
              setTagging(true);
              const response = await axios.post(
                `${API_ENDPOINT}/message/tag-user`,
                payload,
                getHeader()
              );
              toast.success(response.data.message);
              refetch();
            } catch (error) {
              toastError(error);
            } finally {
              setTagging(false);
            }
          }}
          loading={tagging}
          fullWidth
          variant="outlined"
        >
          Tag Contact
        </LoadingButton>
        <Typography mt={2} variant="body1">
          *Please ensure you are in 24 hour session window with recipient to
          send messages
        </Typography>
      </Box>

      {/* Group Selection Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 320 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Select a Group
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Choose a group to look up contact details
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setModalOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {isLoadingList ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} />
            </Box>
          ) : GroupList?.length > 0 ? (
            <List disablePadding>
              {GroupList.map((group, index) => (
                <React.Fragment key={group}>
                  <ListItemButton
                    onClick={() => handleGroupSelect(group)}
                    selected={selectedGroup === group}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      "&.Mui-selected": {
                        bgcolor: "primary.50",
                        "&:hover": { bgcolor: "primary.100" },
                      },
                    }}
                  >
                    <ListItemText
                      primary={group}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                      }}
                    />
                    {selectedGroup === group && (
                      <Chip
                        label="Last selected"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                  {index < GroupList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box p={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No groups available.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LookupProfile;
